import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import 'tom-select/dist/css/tom-select.css'
import '../stylesheets/tom-select.scss'

export default class extends Controller {
  static targets = ["select"]
  static values = {
    url: String,
    applicantId: String,
    competencyType: String
  }

  connect() {
    // Nur TomSelect initialisieren wenn ein Select-Target vorhanden ist
    if (this.hasSelectTarget) {
      // Kleine Verzögerung um sicherzustellen, dass das DOM vollständig geladen ist
      setTimeout(() => {
        this.initializeTomSelect()
      }, 100);
    }
  }

  initializeTomSelect() {
    if (!this.hasSelectTarget) return;

    this.tomselect = new TomSelect(this.selectTarget, {
      valueField: 'id',
      labelField: 'text',
      searchField: ['text'],
      placeholder: "Kompetenz suchen oder neue erstellen...",
      openOnFocus: true,
      preload: 'focus',
      closeAfterSelect: true,
      selectOnTab: true,
      maxOptions: 100,
      create: true,
      createOnBlur: false,
      createFilter: (input) => {
        return input.length >= 2; // Mindestens 2 Zeichen für neue Kompetenz
      },
      create: (input) => {
        return {
          id: 'new_' + input,
          text: `"${input}" erstellen`,
          isNew: true
        };
      },
        load: (query, callback) => {
          if (!query.length) return callback();

          // Verwende die Backend-Such-API mit Competency Type Filter
          const competencyType = this.competencyTypeValue || 'skill';
          const url = `/de/backend/search/?model=competencies&term=${encodeURIComponent(query)}&competency_type=${competencyType}`

          fetch(url)
            .then(response => response.json())
            .then(json => {
              // Prüfe auf ähnliche Kompetenzen für Warnung
              const similarCompetencies = json.filter(item => {
                const itemText = item.text.toLowerCase();
                const queryText = query.toLowerCase();
                
                // Standard-Ähnlichkeit
                if (itemText.includes(queryText) || queryText.includes(itemText)) {
                  return true;
                }
                
                // Spezielle Fälle für ISO-Normen
                if (queryText.includes('din iso') && itemText.includes('iso')) {
                  return true;
                }
                if (queryText.includes('iso') && itemText.includes('din iso')) {
                  return true;
                }
                
                return false;
              });
              
              if (similarCompetencies.length > 0 && query.length >= 3) {
                // Füge Warnung hinzu
                json.unshift({
                  id: 'warning',
                  text: `⚠️ Ähnliche Kompetenzen gefunden: ${similarCompetencies.map(c => c.text).join(', ')}`,
                  disabled: true
                });
              }
              
              callback(json)
            }).catch(() => {
              callback()
            })
        },
      render: {
        no_results: function(data, escape) {
          return '<div class="no-results">Keine Ergebnisse für "' + escape(data.input) + '"</div>';
        },
        option_create: function(data, escape) {
          return '<div class="create">Hinzuf&uuml;gen: <strong>' + escape(data.input) + '</strong>&hellip;</div>';
        },
        option: function(item, escape) {
          if (item.isNew) {
            return `<div class="option text-green-600">
              <span class="title text-sm font-medium">${escape(item.text)}</span>
              <span class="subtext text-xs text-green-500">Neue Kompetenz erstellen</span>
            </div>`
          } else if (item.id === 'warning') {
            return `<div class="option text-yellow-600 bg-yellow-50">
              <span class="title text-sm">${escape(item.text)}</span>
            </div>`
          } else {
            return `<div class="option">
              <span class="title text-sm">${escape(item.text)}</span>
              ${item.subtext ? `<span class="subtext text-xs text-gray-500">${item.subtext}</span>` : ''}
            </div>`
          }
        },
        item: function(item, escape) {
          if (item.isNew) {
            return `<div class="item text-sm text-green-600">${escape(item.text.replace(' erstellen', ''))}</div>`
          } else {
            return `<div class="item text-sm">${escape(item.text)}</div>`
          }
        }
      },
      onChange: (value) => {
        if (value) {
          this.addCompetency(value);
        }
      }
    })
  }


  addCompetency(competencyId) {
    const applicantId = this.applicantIdValue || this.extractApplicantIdFromUrl();
    
    if (!applicantId || applicantId === 'new') {
      this.showError('Bitte speichern Sie den Bewerber zuerst, bevor Sie Kompetenzen hinzufügen.');
      this.tomselect.clear();
      return;
    }
    
    // Prüfe ob es eine neue Kompetenz ist
    if (competencyId.startsWith('new_')) {
      const newCompetencyTitle = competencyId.replace('new_', '');
      this.createCompetency(newCompetencyTitle, applicantId);
    } else {
      // Verwende bestehende Kompetenz
      this.addCompetencyToApplicant(competencyId, applicantId);
    }
  }

  createCompetency(title, applicantId) {
    const url = '/de/backend/competencies';
    const formData = new FormData();
    formData.append('competency[title]', title);
    const competencyType = this.competencyTypeValue || 'skill';
    // Für Skills: immer Tenant-Katalog verwenden
    formData.append('competency_type', competencyType);
    
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.competency && data.competency.id) {
        // Füge die neue Kompetenz zum Applicant hinzu
        this.addCompetencyToApplicant(data.competency.id, applicantId);
      } else {
        this.showError('Fehler beim Erstellen der Kompetenz');
        this.tomselect.clear();
      }
    })
    .catch(error => {
      this.showError('Fehler beim Erstellen der Kompetenz');
      this.tomselect.clear();
    });
  }

  addCompetencyToApplicant(competencyId, applicantId) {
    const url = `/de/backend/applicants/${applicantId}/applicant_competencies`;

    const formData = new FormData();
    formData.append('applicant_competency[competency_id]', competencyId);
    
    // Setze Standard-Gewichtung basierend auf Competency Type
    const competencyType = this.competencyTypeValue || 'skill';
    if (competencyType !== 'language') {
      formData.append('applicant_competency[weight]', '5'); // Nur für andere Kompetenzen
    }
    
    // Die Category wird automatisch über die Competency ID gesetzt
    // formData.append('applicant_competency[competency_category_id]', categoryId);

    this.sendRequest(url, formData);
  }

  getSkillCategoryId() {
    // Finde die Skill-Kategorie ID (normalerweise 1)
    return '1';
  }

  getCompetencyCategoryId(competencyType) {
    // Lade Category IDs dynamisch von der Backend-API
    const categoryMapping = {
      'skill': this.getSkillCategoryId(),
      'language': this.getLanguageCategoryId(), 
      'operation_area': this.getOperationAreaCategoryId()
    };
    return categoryMapping[competencyType] || this.getSkillCategoryId();
  }

  getLanguageCategoryId() {
    // Finde die Language-Kategorie ID
    return '2'; // Diese muss an die tatsächliche ID angepasst werden
  }

  getOperationAreaCategoryId() {
    // Finde die Operation Area-Kategorie ID
    return '3'; // Diese muss an die tatsächliche ID angepasst werden
  }

  extractApplicantIdFromUrl() {
    const urlParts = window.location.pathname.split('/');
    const applicantIndex = urlParts.indexOf('applicants');
    if (applicantIndex === -1) return null;
    return urlParts[applicantIndex + 1];
  }

  sendRequest(url, formData, competencyId = null) {
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => {
      if (response.ok) {
        // Nur TomSelect leeren wenn es existiert
        if (this.tomselect) {
          this.tomselect.clear();
          this.tomselect.clearOptions();
        }
        
        // Verarbeite Turbo Stream Response
        return response.text();
      } else {
        return response.text().then(text => {
          this.showError('Fehler beim Hinzufügen der Kompetenz: ' + response.status);
          throw new Error('Request failed');
        });
      }
    })
    .then(html => {
      if (html) {
        // Verarbeite die Turbo Stream Response
        Turbo.renderStreamMessage(html);
      }
    })
    .catch(error => {
      this.showError('Fehler beim Hinzufügen der Kompetenz');
    });
  }

  showError(message) {
    // Zeige Fehlermeldung - kann später durch Toast-Nachricht ersetzt werden
    alert(message);
  }
}
